import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import parseByDelimiter from '@utils/parseByDelimiter';
import AppSelect from '@components/Global/select';
import useGlobalUser from '@hooks/getGlobalUser';
import apis from '@api/api';


const DataItemSelection = ({ schemaItem, dataSetId, changeCallback, value }: {
  schemaItem?: any
  dataSetId?: string
  changeCallback?: (val) => void
  value?: any
}) => {
  const [items, setItems] = React.useState<{ label: string; value: string }[]>([]);

  // Helper to normalize ObjectId format to plain string
  const normalizeId = (id: any): string | null => {
    if (!id) return null;
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id !== null) {
      // Handle Mongoose ObjectId serialization: { $oid: "..." } or { id: "..." }
      return String(id.$oid || id.id || '');
    }
    return String(id);
  };

  // Initialize selected value from the form field value (passed from DataSetField via ctx.value)
  const initialSelected = normalizeId(value);
  const [selectedDataSet, setSelectedDataSet] = React.useState<string | null>(initialSelected);
  const dataFolderId = dataSetId || schemaItem?.validation?.dataSetId || schemaItem?.content?.dataSetId;

  const { data, isLoading, error } = useQuery({
    queryKey: ['individualData', dataFolderId],
    queryFn: async () => {
      const response = await apis.getDataContent(dataFolderId, 'all');
      return response?.data?.dataContent || [];
    },
    enabled: !!dataFolderId, // Only run query if we have a valid dataFolderId
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const transformed = data
        .filter((item: any) => item && item.data && item._id) // filter invalid entries
        .map((item: any) => {
          // Try to build a meaningful label from available data
          const dataKeys = Object.keys(item.data || {});
          let labelText = '';

          // Try common name fields first
          if (item.data.name || item.data.surname) {
            labelText = `${item.data.name ?? ''} ${item.data.surname ?? ''}`.trim();
          } else if (item.data.title) {
            labelText = item.data.title;
          } else if (item.data.equipment_name) {
            labelText = item.data.equipment_name;
          } else if (dataKeys.length > 0) {
            // Use the first available field value
            labelText = item.data[dataKeys[0]] || 'Unnamed Item';
          } else {
            labelText = `Item ${item._id?.slice(-6)}`;
          }

          // Normalize _id to plain string (handle { $oid: "..." } format)
          const itemId = item._id;
          const idStr = (typeof itemId === 'object' && itemId !== null && itemId.$oid)
            ? itemId.$oid
            : String(itemId);

          return {
            label: parseByDelimiter(labelText),
            value: idStr,
          };
        });
      console.log('Transformed items:', transformed);
      setItems(transformed);

      // Sync selected value now that items are loaded — match against the normalized form value
      if (initialSelected) {
        const matched = transformed.find(i => i.value === initialSelected);
        if (matched) {
          setSelectedDataSet(initialSelected);
        }
      }
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      console.error('Error fetching data items:', error);
    }
  }, [error]);

  // If no dataFolderId, show error message
  if (!dataFolderId) {
    return (
      <AppSelect
        options={[]}
        placeholder="No dataset ID configured"
        value=""
        onValueChange={() => undefined}
        disabled
        minWidth={0}
        style={{ width: '100%' }}
      />
    );
  }

  return (
    <AppSelect
      options={items}
      placeholder={error ? 'Error loading data items' : isLoading ? 'Loading...' : 'Select a data item'}
      value={selectedDataSet ?? ''}
      onValueChange={(next) => {
        const selected = items.find((item) => item.value === next);
        if (selected) {
          setSelectedDataSet(next);
          changeCallback?.(next);
        }
      }}
      searchable
      searchPlaceholder="Search for a data item"
      loading={isLoading}
      disabled={!!error}
      minWidth={0}
      style={{ width: '100%' }}
    />
  );
}
export default DataItemSelection;